import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Button, message, Card, Typography, Tooltip, Modal, Form, Input, Select, Divider, Alert, Tabs, Radio, Checkbox, Avatar, Upload } from 'antd';
import { ReloadOutlined, DeleteOutlined, SendOutlined, PlusOutlined, InfoCircleOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, EyeOutlined, UploadOutlined, PlayCircleOutlined, FilePdfOutlined, NodeIndexOutlined } from '@ant-design/icons';
import apiClient from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const WhatsAppTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [builderType, setBuilderType] = useState('UTILITY'); 
  const [headerType, setHeaderType] = useState('TEXT');
  const [buttons, setButtons] = useState([]);
  const [activeTab, setActiveTab] = useState('content');
  const [activeMainTab, setActiveMainTab] = useState('templates');
  const [media, setMedia] = useState([]);
  const [flows, setFlows] = useState([]);
  const [previewData, setPreviewData] = useState({});
  const [viewTemplate, setViewTemplate] = useState(null);
  const [sendForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [account, setAccount] = useState(null);
  const [selectedWaba, setSelectedWaba] = useState(null);

  useEffect(() => {
    loadAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await apiClient.get('/whatsapp/media');
      if (res.data.success) setMedia(res.data.media);
    } catch (e) {
      console.error('Failed to fetch media:', e);
    }
  };

  const fetchFlows = async (wabaId) => {
    if (!wabaId) return;
    try {
      const res = await apiClient.get(`/whatsapp/flows?wabaId=${wabaId}`);
      if (res.data.success) {
        // Backend returns { success: true, flows: [...] }
        setFlows(res.data.flows || []);
      }
    } catch (e) {
      console.error('Failed to fetch flows:', e);
    }
  };

  const loadAccount = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/accounts/me');
      if (res.data.success) {
        const acc = res.data.account;
        setAccount(acc);
        
        // Pick first active WABA as default
        const firstWaba = acc.whatsappNumbers?.find(n => n.isActive)?.wabaId || acc.cloudApiConfig?.businessAccountId;
        if (firstWaba) {
          setSelectedWaba(firstWaba);
          fetchTemplates(firstWaba);
          fetchMedia();
          fetchFlows(firstWaba);
        }
      }
    } catch (err) {
      message.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async (wabaId) => {
    if (!wabaId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/whatsapp/templates?wabaId=${wabaId}`);
      if (res.data.success) {
        setTemplates(res.data.data || []);
      }
    } catch (error) {
      message.error('Failed to fetch WhatsApp templates: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (name) => {
    if (!selectedWaba) return;
    Modal.confirm({
      title: 'Delete Template',
      content: `Are you sure you want to delete the template "${name}"? This will delete all language versions for the current WABA.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          const res = await apiClient.delete(`/whatsapp/templates/${name}?wabaId=${selectedWaba}`);
          if (res.data.success) {
            message.success('Template deleted');
            fetchTemplates(selectedWaba);
          }
        } catch (error) {
          message.error('Failed to delete: ' + (error.response?.data?.message || error.message));
        }
      }
    });
  };

  const TemplatePreview = ({ data, builderType, headerType, buttons, cards }) => {
    return (
      <div style={{ background: '#e5ddd5', padding: '20px', borderRadius: '12px', minHeight: '400px', position: 'sticky', top: 0 }}>
        <div style={{ background: 'white', borderRadius: '8px', padding: '12px', maxWidth: '280px', boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)', margin: '0 auto' }}>
          {/* Header */}
          {headerType === 'TEXT' && data.headerText && <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#111' }}>{data.headerText}</div>}
          {headerType !== 'NONE' && headerType !== 'TEXT' && (
            <div style={{ background: '#f0f2f5', height: '120px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', overflow: 'hidden', flexDirection: 'column' }}>
              {headerType === 'IMAGE' && data.headerMediaUrl ? (
                <img src={data.headerMediaUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Header" />
              ) : headerType === 'VIDEO' ? (
                <Space direction="vertical" align="center">
                  <PlayCircleOutlined style={{ fontSize: 32, color: '#00a884' }} />
                  <Text size="small" type="secondary">VIDEO PREVIEW</Text>
                </Space>
              ) : headerType === 'DOCUMENT' ? (
                <Space direction="vertical" align="center">
                  <FilePdfOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
                  <Text size="small" type="secondary">PDF DOCUMENT</Text>
                </Space>
              ) : (
                <Text type="secondary">{headerType}</Text>
              )}
            </div>
          )}

          {/* Body */}
          <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', marginBottom: '8px', color: '#111' }}>
            {builderType === 'AUTHENTICATION' ? (
              <span>Your verification code is: <b style={{ color: '#000' }}>123456</b>. For your security, do not share this code.</span>
            ) : (data.bodyText || 'Your message body will appear here...')}
          </div>

          {/* Footer */}
          {(data.footerText || builderType === 'AUTHENTICATION') && !data.isLimitedOffer && (
            <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.45)', marginBottom: '8px' }}>
              {builderType === 'AUTHENTICATION' ? 'For your security, do not share this code.' : data.footerText}
            </div>
          )}

          {/* Buttons */}
          {data.isLimitedOffer ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 0', textAlign: 'center', color: '#00a884', fontWeight: '500', fontSize: '14px' }}>
                Copy Code
              </div>
              <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 0', textAlign: 'center', color: '#00a884', fontWeight: '500', fontSize: '14px' }}>
                Shop Now
              </div>
            </Space>
          ) : (
            buttons.map((btn, i) => (
              <div key={i} style={{ borderTop: '1px solid #f0f0f0', padding: '8px 0', textAlign: 'center', color: '#00a884', fontWeight: '500', fontSize: '14px' }}>
                {btn.text}
              </div>
            ))
          )}
          {builderType === 'AUTHENTICATION' && (
            <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 0', textAlign: 'center', color: '#00a884', fontWeight: '500', fontSize: '14px' }}>
              Copy Code
            </div>
          )}
        </div>
        
        {builderType === 'MARKETING' && data.isLimitedOffer && (
          <div style={{ marginTop: 12, background: '#fff3cd', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', textAlign: 'center', border: '1px solid #ffeeba', maxWidth: '280px', margin: '12px auto 0' }}>
             ⏳ <b>{data.offerText || 'SALE ENDS SOON'}</b> (Countdown Active)
          </div>
        )}
      </div>
    );
  };

  const getValidationErrors = (values) => {
    console.log('Running Compliance Audit on:', values);
    const errors = [];
    const currentCategory = values.category || builderType;
    const isLTO = values.isLimitedOffer && currentCategory === 'MARKETING';

    // 1. Length Validations
    if (!values.name) {
      errors.push('Template Name: Required.');
    } else if (!/^[a-z0-9_]+$/.test(values.name)) {
      errors.push('Template Name: Only lowercase, numbers, and underscores allowed.');
    }
    
    const maxBody = isLTO ? 600 : 1024;
    if (values.bodyText?.length > maxBody) {
      errors.push(`Body Text: Too long (${values.bodyText.length}/${maxBody} chars).`);
    }

    if (values.headerText?.length > 60) errors.push('Header: Max 60 characters allowed.');
    if (values.footerText?.length > 60) errors.push('Footer: Max 60 characters allowed.');

    // 2. Button Validations
    buttons.forEach((btn, i) => {
      if (btn.text?.length > 25) errors.push(`Button ${i+1}: Label "${btn.text}" exceeds 25 characters.`);
      if (btn.type === 'URL' && !btn.url) errors.push(`Button ${i+1}: URL is missing.`);
      if (btn.type === 'PHONE' && (!btn.phone_number || btn.phone_number.length < 8)) {
        errors.push(`Button ${i+1}: Valid Phone Number (with country code) is required.`);
      }
      if (btn.type === 'FLOW' && (!btn.flow_id || !btn.flow_action)) {
        errors.push(`Button ${i+1}: Flow ID and Action are required for Flow buttons.`);
      }
    });

    // 3. Media & LTO Specific Logic
    if (headerType !== 'NONE' && headerType !== 'TEXT') {
      if (!values.headerMediaUrl) {
        errors.push('Media Selection: No asset selected.');
      } else if (!values.headerMediaUrl.startsWith('4::')) {
        errors.push(`Media Audit Failed: Selected ${headerType} is not yet validated by Meta. Please upload it to the Media Library first.`);
      }
    }

    if (isLTO) {
      if (headerType === 'NONE' || headerType === 'TEXT') {
        errors.push('LTO Requirement: Must have an IMAGE or VIDEO header.');
      }
      if (!values.offerText) errors.push('LTO Requirement: Offer badge text is mandatory.');
      if (!values.offerCode) errors.push('LTO Requirement: A Sample Promo Code (e.g. SAVE20) is mandatory.');
      if (!values.ltoUrl) errors.push('LTO Requirement: Destination URL is mandatory.');
      if (buttons.some(b => b.type === 'QUICK_REPLY')) {
        errors.push('LTO Restriction: Quick Replies are not supported with LTO.');
      }
    }

    // 4. Authentication Logic
    if (currentCategory === 'AUTHENTICATION') {
      if (values.headerText) errors.push('Auth Restriction: Headers are not standard for Auth templates.');
    }

    console.log('Audit Results:', errors);
    return errors;
  };

  const handleSendTest = (template) => {
    setSelectedTemplate(template);
    setIsModalVisible(true);
    sendForm.setFieldsValue({ template: template.name });
  };

  const handleCreateTemplate = async (values) => {
    console.log('Form Submit Triggered with values:', values);
    // RUN COMPLIANCE AUDIT FIRST
    const validationErrors = getValidationErrors(values);
    if (validationErrors.length > 0) {
      console.warn('Compliance Audit Failed:', validationErrors);
      return Modal.error({
        title: 'Meta Compliance Audit Failed',
        content: (
          <ul>
            {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        )
      });
    }

    try {
      setLoading(true);
      const formattedName = values.name.toLowerCase().replace(/[^a-z0-9_]/g, '');
      
      const isLTO = values.isLimitedOffer && values.category === 'MARKETING';
      
      const components = [];

      // 1. HEADER (Lowercased for LTO)
      if (headerType !== 'NONE') {
        const hComp = { 
          type: isLTO ? 'header' : 'HEADER', 
          format: isLTO ? headerType.toLowerCase() : headerType 
        };
        if (headerType === 'TEXT') hComp.text = values.headerText;
        else hComp.example = { header_handle: [values.headerMediaUrl] };
        components.push(hComp);
      }

      // 2. BODY (Lowercased for LTO)
      const bodyVars = values.bodyText?.match(/\{\{\d+\}\}/g) || [];
      const bodyComp = { type: isLTO ? 'body' : 'BODY', text: values.bodyText };
      if (bodyVars.length > 0) {
        bodyComp.example = { body_text: [bodyVars.map((_, i) => `Sample ${i+1}`)] };
      }
      if (values.category === 'AUTHENTICATION') {
        bodyComp.add_security_recommendation = true;
        delete bodyComp.text;
      }
      components.push(bodyComp);

      // 3. LIMITED TIME OFFER (Must be lowercase)
      if (isLTO) {
        components.push({
          type: 'limited_time_offer',
          limited_time_offer: {
            has_expiration: true,
            text: (values.offerText || 'Sale Ends Soon').substring(0, 16)
          }
        });
      }

      // 4. FOOTER (NOT ALLOWED IN LTO)
      if (!isLTO && (values.footerText || values.category === 'AUTHENTICATION')) {
        const footerComp = { type: 'FOOTER' };
        if (values.category === 'AUTHENTICATION') {
          footerComp.code_expiration_minutes = 10;
        } else {
          footerComp.text = values.footerText;
        }
        components.push(footerComp);
      }

      // 5. BUTTONS (Lowercased for LTO)
      if (buttons.length > 0 || values.category === 'AUTHENTICATION' || isLTO) {
        let finalButtons = [...buttons];
        if (values.category === 'AUTHENTICATION') {
          finalButtons = [{ type: 'OTP', otp_type: 'COPY_CODE', text: 'Copy Code' }];
        } else if (isLTO) {
          finalButtons = [
            { type: 'copy_code', example: (values.offerCode || 'PROMO20').substring(0, 15) },
            { type: 'url', text: 'Shop Now', url: values.ltoUrl || 'https://example.com' }
          ];
        } else {
          finalButtons = buttons.map(b => {
            const btn = { ...b, type: b.type === 'PHONE' ? 'PHONE_NUMBER' : b.type.toUpperCase() };
            if (b.type === 'FLOW') {
              btn.type = 'FLOW';
              btn.flow_id = b.flow_id;
              btn.flow_action = b.flow_action || 'navigate';
              btn.navigate_screen = b.navigate_screen || 'WELCOME';
            }
            if (b.type === 'OPT_OUT') {
              btn.type = 'QUICK_REPLY'; // Meta uses QR with specific payload for opt-out usually, or MARKETING_OPT_OUT type
            }
            return btn;
          });
        }
        components.push({ type: isLTO ? 'buttons' : 'BUTTONS', buttons: finalButtons });
      }
      
      const payload = {
        wabaId: selectedWaba,
        name: formattedName,
        category: isLTO ? 'marketing' : values.category,
        language: values.language,
        components
      };
      
      const res = await apiClient.post('/whatsapp/templates', payload);
      if (res.data.success) {
        message.success('Template created successfully!');
        setCreateModalVisible(false);
        createForm.resetFields();
        setButtons([]);
        setBuilderType('UTILITY');
        setHeaderType('TEXT');
        setPreviewData({});
        fetchTemplates(selectedWaba);
      }
    } catch (error) {
      message.error(`Meta API Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addButton = (type) => {
    if (buttons.length >= 10) return message.warn('Max 10 buttons total');
    
    let newBtn = { type, text: 'Action' };
    if (type === 'URL') newBtn.url = 'https://example.com';
    if (type === 'PHONE') newBtn.phone_number = '+1234567890';
    if (type === 'CATALOG') newBtn.text = 'View Catalog';
    if (type === 'MPM') newBtn.text = 'View Items';
    if (type === 'FLOW') {
      newBtn.text = 'Open Form';
      newBtn.flow_id = flows[0]?.id || '';
      newBtn.flow_action = 'navigate';
      newBtn.navigate_screen = 'WELCOME';
    }
    
    setButtons([...buttons, newBtn]);
  };

  const handleUploadMedia = async ({ file }) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('type', file.type.startsWith('image') ? 'image' : 'video');

      const res = await apiClient.post('/whatsapp/media', formData);
      if (res.data.success) {
        message.success('Media uploaded successfully!');
        fetchMedia();
      }
    } catch (e) {
      message.error('Upload failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMedia = async (id) => {
    try {
      const res = await apiClient.delete(`/whatsapp/media/${id}`);
      if (res.data.success) {
        message.success('Media deleted');
        fetchMedia();
      }
    } catch (e) {
      message.error('Delete failed');
    }
  };
  const onSendSubmit = async (values) => {
    try {
      setLoading(true);
      // Auto-resolve current phoneNumberId for the selected WABA
      const currentNumber = account?.whatsappNumbers?.find(n => n.wabaId === selectedWaba);
      const phoneNumberId = currentNumber?.phoneNumberId || account?.cloudApiConfig?.phoneNumberId;

      const payload = {
        to: values.to,
        type: 'template',
        fromPhoneNumberId: phoneNumberId,
        template: {
          name: values.template,
          language: { code: selectedTemplate.language },
          components: [] 
        }
      };
      
      const res = await apiClient.post('/whatsapp/message', payload);
      if (res.data.sent) {
        message.success('Test template sent!');
        setIsModalVisible(false);
      }
    } catch (error) {
      message.error('Failed to send: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const mapMetaToPreview = (components) => {
    const data = {};
    let headerType = 'NONE';
    let buttons = [];

    components?.forEach(c => {
      if (c.type === 'HEADER') {
        headerType = c.format;
        if (c.format === 'TEXT') data.headerText = c.text;
        // Handles are not direct URLs, we show a placeholder for library images
        if (c.format === 'IMAGE') data.headerMediaUrl = 'https://api.dicebear.com/7.x/identicon/svg?seed=image';
      }
      if (c.type === 'BODY') data.bodyText = c.text;
      if (c.type === 'FOOTER') data.footerText = c.text;
      if (c.type === 'BUTTONS') buttons = c.buttons || [];
      if (c.type === 'LIMITED_TIME_OFFER') {
        data.isLimitedOffer = true;
        data.offerText = c.limited_time_offer?.text;
      }
    });

    return { data, headerType, buttons };
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'APPROVED': return <Tag color="success" icon={<CheckCircleOutlined />}>APPROVED</Tag>;
      case 'PENDING': return <Tag color="warning" icon={<ClockCircleOutlined />}>PENDING</Tag>;
      case 'REJECTED': return <Tag color="error" icon={<CloseCircleOutlined />}>REJECTED</Tag>;
      default: return <Tag color="default">{status}</Tag>;
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <b>{text}</b>
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      width: 100,
      render: (lang) => <Tag>{lang}</Tag>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (cat) => <Tag color="blue">{cat}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Preview',
      key: 'preview',
      width: 100,
      render: (_, record) => (
        <Button 
          icon={<EyeOutlined />} 
          onClick={() => setViewTemplate(record)}
        >
          View
        </Button>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Send Test">
            <Button 
              type="link" 
              icon={<SendOutlined />} 
              disabled={record.status !== 'APPROVED'}
              onClick={() => handleSendTest(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record.name)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const wabaOptions = account?.whatsappNumbers?.map(n => ({
    label: `${n.displayNumber} (${n.wabaId})`,
    value: n.wabaId
  })) || [];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px' }}>
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Tabs 
          activeKey={activeMainTab} 
          onChange={setActiveMainTab}
          size="large"
          tabBarExtraContent={
            <Space>
              {activeMainTab === 'templates' && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)} disabled={!selectedWaba}>
                  Create Template
                </Button>
              )}
            </Space>
          }
          items={[
            {
              key: 'templates',
              label: <span><ReloadOutlined /> Templates</span>,
              children: (
                <div style={{ padding: '16px 0' }}>
                  <div style={{ marginBottom: 24 }}>
                    <Title level={2}>WhatsApp Cloud Templates</Title>
                    <Paragraph type="secondary">
                      Pre-approved message templates from Meta. Required for starting conversations outside the 24h window.
                    </Paragraph>
                    
                    {!selectedWaba && (
                      <Alert
                        message="Action Required"
                        description="Please select a WhatsApp Business Account (WABA) below to fetch templates."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                      />
                    )}

                    <Space style={{ marginBottom: 24 }}>
                      <Text strong>Select WABA Account:</Text>
                      <Select 
                        style={{ width: 350 }} 
                        options={wabaOptions} 
                        value={selectedWaba}
                        onChange={(val) => {
                          setSelectedWaba(val);
                          fetchTemplates(val);
                          fetchFlows(val);
                        }}
                        placeholder="Select a Business Account"
                      />
                      <Button icon={<ReloadOutlined />} onClick={() => fetchTemplates(selectedWaba)} loading={loading} disabled={!selectedWaba}>Refresh</Button>
                    </Space>
                  </div>

                  <Table 
                    columns={columns} 
                    dataSource={templates} 
                    rowKey="id" 
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: selectedWaba ? 'No templates found for this WABA.' : 'Please select a WABA account.' }}
                  />
                </div>
              )
            },
            {
              key: 'media',
              label: <span><UploadOutlined /> Media Library</span>,
              children: (
                <div style={{ padding: '16px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, alignItems: 'center' }}>
                    <div>
                      <Title level={2}>Media Library</Title>
                      <Text type="secondary">Manage assets for your templates and campaigns</Text>
                    </div>
                    <Upload customRequest={handleUploadMedia} showUploadList={false}>
                      <Button icon={<UploadOutlined />} type="primary" size="large">Upload New Media</Button>
                    </Upload>
                  </div>
                  
                  <Table 
                    dataSource={media}
                    rowKey="_id"
                    columns={[
                      { title: 'Preview', dataIndex: 'url', key: 'url', render: (url, record) => {
                        if (record.type === 'image') return <Avatar shape="square" size={64} src={url} />;
                        if (record.type === 'video') return <Avatar shape="square" size={64} icon={<PlayCircleOutlined />} style={{ background: '#00a884' }} />;
                        return <Avatar shape="square" size={64} icon={<FilePdfOutlined />} style={{ background: '#ff4d4f' }} />;
                      }},
                      { title: 'Name', dataIndex: 'name', key: 'name' },
                      { title: 'Type', dataIndex: 'type', key: 'type', render: t => (
                        <Tag color={t === 'video' ? 'green' : t === 'image' ? 'blue' : 'volcano'}>{t.toUpperCase()}</Tag>
                      )},
                      { title: 'Meta Handle', dataIndex: 'metaHandle', key: 'metaHandle', render: h => h ? <Tag color="green">Ready</Tag> : <Tag>Pending</Tag> },
                      { title: 'Action', key: 'action', render: (_, record) => (
                        <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteMedia(record._id)} />
                      )}
                    ]}
                    loading={loading}
                  />
                </div>
              )
            }
          ]}
        />
      </Card>

      <Modal
        title="🚀 Send Test Template"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Alert 
          message="Basic Test Send"
          description="This UI sends a simple template without variables. Use the Broadcast module for personalized campaigns."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={sendForm} layout="vertical" onFinish={onSendSubmit}>
          <Form.Item name="template" label="Template Name">
            <Input readOnly />
          </Form.Item>
          <Form.Item name="to" label="Recipient Phone Number" rules={[{ required: true, message: 'Digits only, include country code' }]}>
            <Input placeholder="e.g. 91XXXXXXXXXX" />
          </Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />}>
              Send Now
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title={<span><PlusOutlined /> WhatsApp Pro Template Builder</span>}
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); setButtons([]); }}
        onOk={() => createForm.submit()}
        okText="Submit to Meta"
        confirmLoading={loading}
        width={1100}
        style={{ top: 20 }}
      >
        <div style={{ display: 'flex', gap: '32px' }}>
          {/* Left: Input Form */}
          <div style={{ flex: '1.5' }}>
            <Form 
              form={createForm} 
              layout="vertical" 
              onFinish={handleCreateTemplate} 
              onFinishFailed={(err) => {
                console.warn('Form Validation Failed:', err);
                message.error('Please check the form for missing or invalid fields.');
              }}
              initialValues={{ category: 'UTILITY', language: 'en_US', headerType: 'TEXT' }}
              onValuesChange={(changed, all) => {
                const updated = { ...all };
                if (changed.name) {
                  // Advanced normalization: replace spaces/symbols with underscore, then lowercase
                  const normalized = changed.name
                    .replace(/[^a-zA-Z0-9]/g, '_')
                    .replace(/_{2,}/g, '_')
                    .toLowerCase();
                  
                  if (normalized !== changed.name) {
                    createForm.setFieldsValue({ name: normalized });
                    updated.name = normalized;
                  }
                }

                if (changed.isLimitedOffer && changed.isLimitedOffer === true) {
                  // LTO Force: Must be Image or Video
                  if (all.headerType === 'TEXT' || all.headerType === 'NONE') {
                    createForm.setFieldsValue({ headerType: 'IMAGE' });
                    updated.headerType = 'IMAGE';
                    setHeaderType('IMAGE');
                    message.info('LTO Templates require an Image or Video header. Switched to Image.');
                  }
                }

                setPreviewData(updated);
                if (changed.category) setBuilderType(changed.category);
                if (changed.headerType) setHeaderType(changed.headerType);
              }}
            >
              <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
                {
                  key: 'content',
                  label: '1. Core Content',
                  children: (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                        <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
                          <Input placeholder="e.g. flash_sale_2025" />
                        </Form.Item>
                        <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                          <Select onChange={setBuilderType} options={[
                            { label: 'Marketing', value: 'MARKETING' },
                            { label: 'Utility', value: 'UTILITY' },
                            { label: 'Authentication', value: 'AUTHENTICATION' }
                          ]} />
                        </Form.Item>
                        <Form.Item name="language" label="Language" rules={[{ required: true }]}>
                          <Select 
                            showSearch
                            placeholder="Select language"
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            options={[
                              { label: 'English (US)', value: 'en_US' },
                              { label: 'English (UK)', value: 'en_GB' },
                              { label: 'Hindi', value: 'hi' },
                              { label: 'Spanish', value: 'es' },
                              { label: 'Arabic', value: 'ar' },
                              { label: 'Portuguese (BR)', value: 'pt_BR' },
                              { label: 'Portuguese (PT)', value: 'pt_PT' },
                              { label: 'French', value: 'fr' },
                              { label: 'German', value: 'de' },
                              { label: 'Italian', value: 'it' },
                              { label: 'Chinese (Simplified)', value: 'zh_CN' },
                              { label: 'Chinese (Traditional)', value: 'zh_HK' },
                              { label: 'Japanese', value: 'ja' },
                              { label: 'Korean', value: 'ko' },
                              { label: 'Russian', value: 'ru' },
                              { label: 'Turkish', value: 'tr' },
                              { label: 'Bengali', value: 'bn' },
                              { label: 'Gujarati', value: 'gu' },
                              { label: 'Kannada', value: 'kn' },
                              { label: 'Malayalam', value: 'ml' },
                              { label: 'Marathi', value: 'mr' },
                              { label: 'Punjabi', value: 'pa' },
                              { label: 'Tamil', value: 'ta' },
                              { label: 'Telugu', value: 'te' }
                            ]} 
                          />
                        </Form.Item>
                      </div>

                      <Form.Item label="Header Selection">
                        <Radio.Group value={headerType} onChange={e => setHeaderType(e.target.value)}>
                          <Radio.Button value="TEXT" disabled={previewData.isLimitedOffer}>Text Header</Radio.Button>
                          <Radio.Button value="IMAGE">Image</Radio.Button>
                          <Radio.Button value="VIDEO">Video</Radio.Button>
                          <Radio.Button value="DOCUMENT">Doc</Radio.Button>
                          <Radio.Button value="NONE" disabled={previewData.isLimitedOffer}>None</Radio.Button>
                        </Radio.Group>
                      </Form.Item>

                      {headerType === 'TEXT' ? (
                        <Form.Item name="headerText" label="Header Title (Max 60 chars)">
                          <Input placeholder="e.g. Special Promotion" maxLength={60} />
                        </Form.Item>
                      ) : headerType !== 'NONE' && (
                          <>
                            <Form.Item name="headerMediaUrl" label={`Select ${headerType} Asset`} rules={[{ required: true }]}>
                              <Select 
                                placeholder={`Choose a ${headerType.toLowerCase()}...`}
                                dropdownRender={menu => (
                                  <>
                                    {menu}
                                    <Divider style={{ margin: '8px 0' }} />
                                    <div style={{ padding: '0 8px 4px' }}>
                                      <Button 
                                        type="link" 
                                        size="small" 
                                        icon={<PlusOutlined />} 
                                        onClick={() => {
                                          setCreateModalVisible(false);
                                          setActiveMainTab('media');
                                        }}
                                      >
                                        Upload in Media Library
                                      </Button>
                                    </div>
                                  </>
                                )}
                              >
                                {media
                                  .filter(m => {
                                    if (headerType === 'IMAGE') return m.type === 'image';
                                    if (headerType === 'VIDEO') return m.type === 'video';
                                    if (headerType === 'DOCUMENT') return m.type === 'document' || m.mimeType?.includes('pdf');
                                    return true;
                                  })
                                  .map(m => (
                                  <Option key={m._id} value={m.metaHandle} disabled={!m.metaHandle}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Space>
                                        {m.type === 'image' ? <Avatar size="small" src={m.url} /> : 
                                         m.type === 'video' ? <PlayCircleOutlined /> : <FilePdfOutlined />}
                                        <span style={{ color: !m.metaHandle ? '#bfbfbf' : 'inherit' }}>{m.name}</span>
                                      </Space>
                                      {m.metaHandle ? <Tag color="green" style={{ fontSize: 10 }}>Ready</Tag> : <Tag style={{ fontSize: 10 }}>Not Validated</Tag>}
                                    </div>
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Alert 
                              type="info" 
                              showIcon 
                              message="Media Handles" 
                              description="Selected assets will use their Meta-validated handles automatically during template creation." 
                              style={{ marginBottom: 16 }}
                            />
                          </>
                      )}

                      <Form.Item name="bodyText" label="Main Body Text" rules={[{ required: builderType !== 'AUTHENTICATION' }]}>
                        <TextArea rows={5} placeholder="Use {{1}}, {{2}} for variables" />
                      </Form.Item>

                      <Form.Item name="footerText" label="Footer Text (Optional)">
                        <Input 
                          placeholder={previewData.isLimitedOffer ? "NOT SUPPORTED IN LTO" : "Small gray text at bottom"} 
                          maxLength={60} 
                          disabled={previewData.isLimitedOffer}
                        />
                      </Form.Item>
                    </div>
                  )
                },
                {
                  key: 'interactive',
                  label: '2. Buttons & Offers',
                  children: (
                    <div>
                      <div style={{ marginBottom: 12 }}>
                        <Title level={5}>Interactivity (Buttons)</Title>
                        <Space wrap>
                          <Button size="small" onClick={() => addButton('QUICK_REPLY')}>+ Quick Reply</Button>
                          <Button size="small" onClick={() => addButton('URL')}>+ Website URL</Button>
                          <Button size="small" onClick={() => addButton('PHONE')}>+ Call Phone</Button>
                          {(builderType === 'MARKETING' || builderType === 'UTILITY') && (
                            <Button size="small" onClick={() => addButton('FLOW')} icon={<NodeIndexOutlined />}>+ WhatsApp Flow</Button>
                          )}
                          <Button size="small" onClick={() => addButton('CATALOG')}>+ Catalog</Button>
                        </Space>
                      </div>

                      {buttons.map((btn, i) => (
                        <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Button ${i + 1}: ${btn.type}`} extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setButtons(buttons.filter((_, idx) => idx !== i))} />}>
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Input value={btn.text} addonBefore="Label" onChange={(e) => {
                              const newBtns = [...buttons];
                              newBtns[i].text = e.target.value;
                              setButtons(newBtns);
                            }} />
                            
                            {btn.type === 'URL' && (
                              <Input value={btn.url} addonBefore="URL" onChange={(e) => {
                                const newBtns = [...buttons];
                                newBtns[i].url = e.target.value;
                                setButtons(newBtns);
                              }} />
                            )}
                            
                            {btn.type === 'PHONE' && (
                              <Input value={btn.phone_number} addonBefore="Phone" onChange={(e) => {
                                const newBtns = [...buttons];
                                newBtns[i].phone_number = e.target.value;
                                setButtons(newBtns);
                              }} />
                            )}

                            {btn.type === 'FLOW' && (
                              <Space direction="vertical" style={{ width: '100%' }}>
                                <Select 
                                  style={{ width: '100%' }}
                                  placeholder="Select a Flow"
                                  value={btn.flow_id}
                                  onChange={(val) => {
                                    const newBtns = [...buttons];
                                    newBtns[i].flow_id = val;
                                    setButtons(newBtns);
                                  }}
                                  options={flows.map(f => ({ label: `${f.name} (${f.id})`, value: f.id }))}
                                />
                                <Input 
                                  value={btn.navigate_screen} 
                                  addonBefore="Start Screen ID" 
                                  placeholder="WELCOME"
                                  onChange={(e) => {
                                    const newBtns = [...buttons];
                                    newBtns[i].navigate_screen = e.target.value;
                                    setButtons(newBtns);
                                  }} 
                                />
                              </Space>
                            )}
                            </Space>
                          </Card>
                        ))}


                      {builderType === 'MARKETING' && (
                        <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                          <Form.Item name="isLimitedOffer" valuePropName="checked">
                            <Checkbox><Text strong>Enable Limited Time Offer</Text></Checkbox>
                          </Form.Item>
                          {previewData.isLimitedOffer && (
                            <div style={{ padding: '12px', background: '#fff7e6', borderRadius: '8px', border: '1px solid #ffd591' }}>
                              <Form.Item name="offerText" label="Offer Badge Text" rules={[{ required: true }]}>
                                <Input placeholder="e.g. Flash Sale (Max 16 chars)" maxLength={16} />
                              </Form.Item>
                              <Form.Item name="offerCode" label="Sample Promo Code (for 'Copy Code' button)" rules={[{ required: true }]}>
                                <Input placeholder="e.g. SAVE20 (Max 15 chars)" maxLength={15} />
                              </Form.Item>
                              <Form.Item name="ltoUrl" label="Destination Website URL" rules={[{ required: true }]}>
                                <Input placeholder="https://yourstore.com" />
                              </Form.Item>
                              <Alert type="info" message="In LTO mode, Meta automatically adds 'Copy Code' as the 1st button." showIcon />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                }
              ]} />
            </Form>
            
            {/* Validation Feedback */}
            <div style={{ marginTop: 20 }}>
              {getValidationErrors(previewData).length > 0 ? (
                <Alert
                  message="Meta Compliance Requirements"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {getValidationErrors(previewData).map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  }
                  type="warning"
                  showIcon
                />
              ) : (
                <Alert 
                  message="Ready for Meta Approval" 
                  description={previewData.isLimitedOffer ? "Note: Any footer text will be auto-removed to comply with LTO rules." : "All structure rules met."}
                  type="success" 
                  showIcon 
                />
              )}
            </div>
          </div>

          {/* Right: Live Preview & Compliance */}
          <div style={{ flex: '1', borderLeft: '1px solid #f0f0f0', paddingLeft: '32px' }}>
            <div style={{ marginBottom: 24, padding: 16, background: '#f8f9fa', borderRadius: 12, border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text strong style={{ fontSize: 13, color: '#495057' }}>COMPLIANCE & FIX-IT GUIDE</Text>
                <Tag color={getValidationErrors(previewData).length === 0 ? 'success' : 'error'}>
                  {getValidationErrors(previewData).length === 0 ? 'VALID' : 'ACTION REQUIRED'}
                </Tag>
              </div>
              
              {getValidationErrors(previewData).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {getValidationErrors(previewData).map((err, i) => (
                    <Alert 
                      key={i}
                      message={<span style={{ fontSize: 12 }}>{err}</span>}
                      type="error"
                      showIcon
                      action={
                        <Tooltip title="Meta Policy requires specific formatting for this template type. Ensure names are lowercase and media is pre-validated.">
                          <Button size="small" type="ghost" icon={<InfoCircleOutlined />}>How to fix?</Button>
                        </Tooltip>
                      }
                    />
                  ))}
                </div>
              ) : (
                <Alert 
                  message="Excellent! Your template meets all Meta Technical Requirements."
                  type="success"
                  showIcon
                />
              )}
            </div>

            <div style={{ textAlign: 'center', marginBottom: 16 }}><Text type="secondary" strong>LIVE WHATSAPP PREVIEW</Text></div>
            <TemplatePreview 
              data={previewData} 
              builderType={builderType} 
              headerType={headerType} 
              buttons={buttons} 
            />
          </div>
        </div>
      </Modal>

      {/* Template Preview Modal */}
      <Modal
        title={`Preview: ${viewTemplate?.name}`}
        open={!!viewTemplate}
        onCancel={() => setViewTemplate(null)}
        footer={[
          <Button key="close" onClick={() => setViewTemplate(null)}>Close</Button>
        ]}
        width={450}
        centered
      >
        {viewTemplate && (() => {
          const { data, headerType, buttons } = mapMetaToPreview(viewTemplate.components);
          return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <TemplatePreview 
                data={data} 
                builderType={viewTemplate.category} 
                headerType={headerType} 
                buttons={buttons} 
              />
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default WhatsAppTemplateManager;
